import {
    Brand,
    OtherExternalTeamManagement,
    OtherInternalTeamManagement,
    OwnTeamManagement,
    PermissionGroup,
    CampaignManagement,
    CampaignSubmission,
    Banner,
    Payout,
    CampaignStatusControl,
    TemplatedCampaignManagement,
    Ambassador,
} from 'components/Shared/Permission/enums'
import { TFunction } from 'i18next'

interface PermissionLabel {
    description: string
    info: string
}

interface PermissionLabelMap {
    [key: string]: PermissionLabel
}

export const getPermissionGroupTranslation = (t: TFunction, code: string, isInternal: boolean): PermissionLabel => {
    const permissionMapping: PermissionLabelMap = {
        [PermissionGroup.OwnTeam]: {
            description: t('manageTeams.rolesAndPermissions.teamManagementTitle'),
            info: t('manageTeams.rolesAndPermissions.teamManagementTooltip'),
        },
        [PermissionGroup.OtherInternalTeam]: {
            description: t('manageTeams.rolesAndPermissions.internalTeamManagementTitle'),
            info: t('manageTeams.rolesAndPermissions.internalTeamManagementTooltip'),
        },
        [PermissionGroup.OtherExternalTeam]: {
            description: t('manageTeams.rolesAndPermissions.clientTeamManagementTitle'),
            info: t('manageTeams.rolesAndPermissions.clientTeamManagementTooltip'),
        },
        [PermissionGroup.Brand]: {
            description: isInternal
                ? t('manageTeams.rolesAndPermissions.clientBrandCreationTitle')
                : t('manageTeams.rolesAndPermissions.brandCreationTitle'),
            info: isInternal
                ? t('manageTeams.rolesAndPermissions.clientBrandCreationTooltip')
                : t('manageTeams.rolesAndPermissions.brandCreationTooltip'),
        },
        [PermissionGroup.AmbassadorsManagement]: {
            description: t('manageTeams.rolesAndPermissions.manageAmbassadorsTitle'),
            info: t('manageTeams.rolesAndPermissions.manageAmbassadorsSetTooltips'),
        },
        [PermissionGroup.CampaignManagement]: {
            description: t('manageTeams.rolesAndPermissions.campaignEditorManagementTitle'),
            info: t('manageTeams.rolesAndPermissions.campaignManagementOnCampaignEditorTooltip'),
        },
        [PermissionGroup.TemplatedCampaign]: {
            description: t('manageTeams.rolesAndPermissions.templateManagementTitle'),
            info: t('manageTeams.rolesAndPermissions.campaignManagementOnTemplateTooltip'),
        },
        [PermissionGroup.CampaignStatusControl]: {
            description: t('manageTeams.rolesAndPermissions.campaginStatusControlTitle'),
            info: t('manageTeams.rolesAndPermissions.campaignStatusControlTooltip'),
        },
        [PermissionGroup.Submission]: {
            description: t('manageTeams.rolesAndPermissions.submissionApprovalTitle'),
            info: t('manageTeams.rolesAndPermissions.submissionApprovalTooltip'),
        },
        [PermissionGroup.Banner]: {
            description: t('manageTeams.rolesAndPermissions.bannerManagementTitle'),
            info: t('manageTeams.rolesAndPermissions.bannerManagementTooltip'),
        },
        [PermissionGroup.Payout]: {
            description: t('manageTeams.rolesAndPermissions.payoutManagementTitle'),
            info: t('manageTeams.rolesAndPermissions.payoutManagementTooltip'),
        },
    }
    if (permissionMapping[code]) {
        return permissionMapping[code]
    } else {
        return {
            description: code,
            info: code,
        }
    }
}

export const getPermissionTranslation = (t: TFunction, code: string, isInternal: boolean): PermissionLabel => {
    const permissionMapping: PermissionLabelMap = {
        [OwnTeamManagement.AssignRoles]: {
            description: t('manageTeams.rolesAndPermissions.manageTeamMembersTitle'),
            info: t('manageTeams.rolesAndPermissions.manageTeamMembersTooltip'),
        },
        [OwnTeamManagement.ManageRoles]: {
            description: t('manageTeams.rolesAndPermissions.manageRolesTitle'),
            info: t('manageTeams.rolesAndPermissions.manageRolesTooltip'),
        },
        [OtherInternalTeamManagement.AssignRoles]: {
            description: t('manageTeams.rolesAndPermissions.manageInternalTeamMemberTitle'),
            info: t('manageTeams.rolesAndPermissions.manageInternalTeamMemberTooltip'),
        },
        [OtherInternalTeamManagement.ManageRoles]: {
            description: t('manageTeams.rolesAndPermissions.manageRolesInternalTeamTitle'),
            info: t('manageTeams.rolesAndPermissions.manageRolesInternalTeamTooltip'),
        },
        [OtherExternalTeamManagement.AssignRoles]: {
            description: t('manageTeams.rolesAndPermissions.createManageClientsTeamsTitle'),
            info: t('manageTeams.rolesAndPermissions.createManageClientsTeamsTooltip'),
        },
        [OtherExternalTeamManagement.ManageRoles]: {
            description: t('manageTeams.rolesAndPermissions.manageRoleClientTitle'),
            info: t('manageTeams.rolesAndPermissions.editTeamTpyeRolesTooltips'),
        },
        [OwnTeamManagement.CreateTeam]: {
            description: t('manageTeams.rolesAndPermissions.createNewTeamsTitle'),
            info: t('manageTeams.rolesAndPermissions.createNewTeamsTooltip'),
        },
        [Brand.CreateEdit]: {
            description: isInternal
                ? t('manageTeams.rolesAndPermissions.createEditClientBrandsTitle')
                : t('manageTeams.rolesAndPermissions.createEditBrandsTitle'),
            info: isInternal
                ? t('manageTeams.rolesAndPermissions.createEditClientBrandsTooltip')
                : t('manageTeams.rolesAndPermissions.createEditBrandsTooltip'),
        },
        [Brand.Delete]: {
            description: isInternal
                ? t('manageTeams.rolesAndPermissions.deleteClientBrandsTitle')
                : t('manageTeams.rolesAndPermissions.deleteBrandsTitle'),
            info: isInternal
                ? t('manageTeams.rolesAndPermissions.deleteClientBrandsTooltip')
                : t('manageTeams.rolesAndPermissions.deleteBrandsTooltip'),
        },
        [CampaignManagement.LiveCampaignEditDeadlines]: {
            description: t('manageTeams.rolesAndPermissions.editLiveCampaignDeadlineTitle'),
            info: t('manageTeams.rolesAndPermissions.editLiveCampaignDeadlineTooltip'),
        },
        [CampaignManagement.LiveCampaignEditBriefs]: {
            description: t('manageTeams.rolesAndPermissions.editLiveCampaignBriefTitle'),
            info: t('manageTeams.rolesAndPermissions.editLiveCampaignBriefTooltip'),
        },
        [CampaignManagement.EditDefaultTags]: {
            description: t('manageTeams.rolesAndPermissions.editDefaultTagsTitle'),
            info: t('manageTeams.rolesAndPermissions.editDefaultTagsTooltip'),
        },
        [CampaignStatusControl.LaunchCampaign]: {
            description: t('manageTeams.rolesAndPermissions.editRejectLaunchCampaignTitle'),
            info: t('manageTeams.rolesAndPermissions.editRejectLaunchCampaignTooltip'),
        },
        [CampaignManagement.CreateAndEditBriefsForExternal]: {
            description: t('manageTeams.rolesAndPermissions.createOnCampaignEditorTitle'),
            info: t('manageTeams.rolesAndPermissions.createOnCampaignEditorTooltips'),
        },
        [CampaignManagement.CreateAndEditBriefsForInternal]: {
            description: t('manageTeams.rolesAndPermissions.createOnCampaignEditorTitle'),
            info: t('manageTeams.rolesAndPermissions.createOnCampaignEditorTooltips'),
        },
        [TemplatedCampaignManagement.CreateAndEditBriefsForExternal]: {
            description: t('manageTeams.rolesAndPermissions.createOnTemplateTitle'),
            info: t('manageTeams.rolesAndPermissions.createOnTemplateTooltips'),
        },
        [TemplatedCampaignManagement.CreateAndEditBriefsForInternal]: {
            description: t('manageTeams.rolesAndPermissions.createOnTemplateTitle'),
            info: t('manageTeams.rolesAndPermissions.createOnTemplateTooltips'),
        },
        [TemplatedCampaignManagement.LiveCampaignEditBriefsAndDeadlines]: {
            description: t('manageTeams.rolesAndPermissions.editLiveBriefAndSceduleTitle'),
            info: t('manageTeams.rolesAndPermissions.editLiveBriefAndSchduleOnTemplateTooltips'),
        },
        [CampaignManagement.ShowPrivateCampaignBudget]: {
            description: t('manageTeams.rolesAndPermissions.viewCampaignBudgetTitle'),
            info: t('manageTeams.rolesAndPermissions.viewCampaignBudgetTooltip'),
        },
        [CampaignStatusControl.CloseCampaign]: {
            description: t('manageTeams.rolesAndPermissions.closeCampaignTitle'),
            info: t('manageTeams.rolesAndPermissions.closeCampaignTooltip'),
        },
        [CampaignStatusControl.ReviewCampaign]: {
            description: t('manageTeams.roleAndPermissions.reviewCampaignTitle'),
            info: t('manageTeams.rolesAndPermissions.reviewCampaignTooltip'),
        },
        [CampaignStatusControl.PayCampaign]: {
            description: t('manageTeams.roleAndPermissions.payCampaignTitle'),
            info: t('manageTeams.roleAndPermissions.payCampaignTooltips'),
        },
        [CampaignSubmission.ApproveRegistrationAndManageWaitList]: {
            description: t('manageTeams.rolesAndPermissions.approveRegistrationTitle'),
            info: t('manageTeams.rolesAndPermissions.approveRegistrationTooltip'),
        },
        [CampaignSubmission.MoveRegistrationToPendingApproval]: {
            description: t('manageTeams.rolesAndPermissions.moveRegistrationTitle'),
            info: t('manageTeams.rolesAndPermissions.moveRegistrationTooltip'),
        },
        [CampaignSubmission.ReviewDirectly]: {
            description: t('manageTeams.rolesAndPermissions.approveRejectSubmissionsTitle'),
            info: t('manageTeams.rolesAndPermissions.approveRejectSubmissionsTooltip'),
        },
        [CampaignSubmission.ReviewIndirectly]: {
            description: t('manageTeams.rolesAndPermissions.requestReviewsTitle'),
            info: t('manageTeams.rolesAndPermissions.requestReviewsTooltip'),
        },
        [CampaignSubmission.ReviewUrlOrScreenshotStep]: {
            description: t('manageTeams.rolesAndPermissions.reviewUrlTitle'),
            info: t('manageTeams.rolesAndPermissions.reviewUrlTooltip'),
        },
        [CampaignSubmission.ReviewEngagementStep]: {
            description: t('manageTeams.rolesAndPermissions.reviewEngagementResultsTitle'),
            info: t('manageTeams.rolesAndPermissions.reviewEngagementResultsTooltip'),
        },
        [CampaignSubmission.ManageTag]: {
            description: t('manageTeams.rolesAndPermissions.addTagViewTagHistoryTitle'),
            info: t('manageTeams.rolesAndPermissions.addTagViewTagHistroyTooltip'),
        },
        [CampaignSubmission.EditPostSchedule]: {
            description: t('manageTeams.rolesAndPermissions.updatePostScheduleTitle'),
            info: t('manageTeams.rolesAndPermissions.updatePostScheduleTooltip'),
        },

        [Banner.CreateBanner]: {
            description: t('manageTeams.rolesAndPermissions.createBannerTitle'),
            info: t('manageTeams.rolesAndPermissions.createBannerTooltip'),
        },
        [Banner.DisableBanner]: {
            description: t('manageTeams.rolesAndPermissions.disableBannerTitle'),
            info: t('manageTeams.rolesAndPermissions.disableBannerTooltip'),
        },
        [Payout.UpdatePayouts]: {
            description: t('manageTeams.rolesAndPermissions.updatePayoutTitle'),
            info: t('manageTeams.rolesAndPermissions.updatePayoutTooltip'),
        },
        [Payout.PayPartiposters]: {
            description: t('manageTeams.rolesAndPermissions.payPartipostersTitle'),
            info: t('manageTeams.rolesAndPermissions.payPartipostersTooltip'),
        },
        [Payout.DisqualifyPartiposters]: {
            description: t('manageTeams.rolesAndPermissions.disqualifyPartipostersTitle'),
            info: t('manageTeams.rolesAndPermissions.disqualifyPartipostersTooltip'),
        },
        [Ambassador.AddRemoveFavourite]: {
            description: t('manageTeams.rolesAndPermissions.addRemoveFavouriteTitle'),
            info: t('manageTeams.rolesAndPermissions.addRemoveFavouriteTooltips'),
        },
    }
    if (permissionMapping[code]) {
        return permissionMapping[code]
    } else {
        return {
            description: code,
            info: code,
        }
    }
}
